/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 * All Rights Reserved
 ****************************************************************************************
 */

import { useState } from 'react';
import {
    formatCurrency,
    formatDateInCompanyTimeZone
} from '../../../../../../utils/format';

const OrderDetailsCard = ({
    orderDetails,
    remainingTime,
    timerText,
    timerCls,
    companyState,
    operatingLocationCode,
    defaultProductImage,
}) => {
    const [recurringExpanded, setRecurringExpanded] = useState(false);

    if (!orderDetails) return null;

    const isGrouped =
        orderDetails?.isGroupedOrder === true &&
        Array.isArray(orderDetails?.childOrders) &&
        orderDetails.childOrders.length > 0;

    const tc = timerCls;

    const formatDateWithCompanyTimezone = (dateString) => {
        if (!dateString) return '';
        return formatDateInCompanyTimeZone(
            dateString,
            companyState || operatingLocationCode,
            'MMMM d, yyyy, h:mm a zzz',
            dateString
        );
    };

    const getUserAddresses = () => {
        const addresses = [];
        if (companyState?.address?.streetName1) {
            addresses.push({ type: 'company', label: companyState.name, ...companyState.address });
        }
        const orderCompany = isGrouped ? orderDetails.company : orderDetails.orderDetails?.[0]?.company;
        if (orderCompany?.address?.streetName1) {
            const alreadyAdded = addresses.some(a => a.streetName1 === orderCompany.address.streetName1);
            if (!alreadyAdded) {
                addresses.push({ type: 'order-company', label: orderCompany.name, ...orderCompany.address });
            }
        }
        return addresses;
    };

    const allAddresses = getUserAddresses();
    const formatLocalWeekday = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { weekday: 'short' });
    };

    const formatLocalDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatLocalTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const formatSlotRange = (lineItem) => {
        const period = lineItem?.scheduledProduct?.period;
        if (!period?.effectiveDate?.localStartDate) return null;
        try {
            const { localStartDate, localEndDate } = period.effectiveDate;
            return {
                weekday: formatLocalWeekday(localStartDate),
                date: formatLocalDate(localStartDate),
                time: `${formatLocalTime(localStartDate)} – ${formatLocalTime(localEndDate)}`,
            };
        } catch { return null; }
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 fx-m-card">

                {/* ── Card Header with timer ── */}
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-slate-800">Booking Details</h2>
                            <p className="text-xs text-slate-400">
                                {isGrouped
                                    ? `Group Order #${orderDetails.number} · ${orderDetails.childOrders.length} recurring sessions`
                                    : `Order #${orderDetails.orderDetails?.[0]?.number || 'N/A'}`}
                            </p>
                        </div>
                    </div>
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold ${tc.pill}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${tc.dot} ${remainingTime?.minutes < 5 ? 'animate-pulse' : ''}`} />
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {timerText}
                    </div>
                </div>

                {/* ══════════════════════════════════════════════
                    SINGLE ORDER VIEW
                ══════════════════════════════════════════════ */}
                {!isGrouped && orderDetails.orderDetails?.[0] && (() => {
                    const order = orderDetails.orderDetails[0];
                    const li = order.lineItemList?.[0];
                    const sp = li?.scheduledProduct;
                    const product = sp?.product;

                    const getProductImage = () => {
                        if (product?.imageContents?.[0]?.displayLink) return product.imageContents[0].displayLink;
                        if (product?.imageUrlsList?.[0]) {
                            return product.imageUrlsList[0].startsWith('http')
                                ? product.imageUrlsList[0]
                                : `https://managemybiz.s3.amazonaws.com/${product.imageUrlsList[0]}`;
                        }
                        return product?.category?.imageURL || null;
                    };

                    const formatBookingRange = () => {
                        const { localStartDate, localEndDate } = sp?.period?.effectiveDate || {};
                        if (!localStartDate) return '';
                        try {
                            return `${formatLocalDate(localStartDate)}, ${formatLocalTime(localStartDate)} – ${formatLocalTime(localEndDate)}`;
                        } catch { return ''; }
                    };

                    const img = getProductImage();
                    const name = product?.name || sp?.name || 'Workspace';

                    return (
                        <div className="mt-2">
                            {img && (
                                <div className="mb-5 rounded-2xl overflow-hidden border border-slate-100 shadow-sm group relative">
                                    <img src={img} alt={name} className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105" onError={(e) => e.target.style.display = 'none'} />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-4">
                                        <h3 className="text-white font-bold text-base">{name}</h3>
                                        {product?.category?.name && (
                                            <span className="inline-block mt-1 px-2.5 py-0.5 bg-white/20 backdrop-blur text-white text-xs rounded-full border border-white/30">
                                                {product.category.name}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="space-y-3">
                                <div className="flex justify-between items-center py-3 px-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <span className="text-slate-500 text-sm font-medium flex items-center gap-2">
                                        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        Booking Date
                                    </span>
                                    <span className="text-slate-800 text-sm font-semibold">{formatDateWithCompanyTimezone(order.createdDate)}</span>
                                </div>
                                {sp?.period?.effectiveDate && (
                                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="font-semibold text-blue-800 text-sm">Booking Schedule</span>
                                        </div>
                                        <p className="text-blue-700 text-sm font-medium pl-6">{formatBookingRange()}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* ══════════════════════════════════════════════
                    GROUPED / RECURRING ORDER VIEW  (redesigned)
                ══════════════════════════════════════════════ */}
                {isGrouped && (() => {
                    const children = orderDetails.childOrders;
                    const firstLI = children[0]?.lineItemList?.[0];
                    const sp = firstLI?.scheduledProduct;
                    const product = sp?.product;
                    const productName = product?.name || sp?.name || 'Workspace';
                    const categoryName = product?.category?.name || '';
                    const image = product?.imageContents?.[0]?.displayLink || defaultProductImage;
                    const dur = sp?.period?.duration;
                    const pph = parseFloat(product?.price?.priceAmt) || 0;

                    const firstSlot = formatSlotRange(firstLI);
                    const lastSlot = formatSlotRange(children[children.length - 1]?.lineItemList?.[0]);

                    const parentTotal = children.reduce(
                        (sum, c) => sum + parseFloat(c.dueAmount ?? c.totalAmount ?? 0), 0
                    );
                    const fmtAmt = (v) => formatCurrency(v, companyState || operatingLocationCode);

                    return (
                        <div className="mt-2 space-y-4">

                            {/* ── Hero banner ── */}
                            <div className="relative rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
                                {image
                                    ? <img src={image} alt={productName} className="w-full h-44 object-cover" onError={(e) => e.target.style.display = 'none'} />
                                    : (
                                        <div className="w-full h-44 bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
                                            <svg className="w-12 h-12 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16" />
                                            </svg>
                                        </div>
                                    )
                                }
                                {/* gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent" />

                                {/* Recurring badge top-left */}
                                <div className="absolute top-3 left-3">
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-white text-xs font-semibold">
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Recurring
                                    </span>
                                </div>

                                {/* Session count badge top-right */}
                                <div className="absolute top-3 right-3">
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-500/90 backdrop-blur-sm text-white text-xs font-bold shadow-lg">
                                        {children.length} sessions
                                    </span>
                                </div>

                                {/* Bottom content */}
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                    <h3 className="text-white font-bold text-base leading-snug">{productName}</h3>
                                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                        {categoryName && (
                                            <span className="px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs border border-white/25">
                                                {categoryName}
                                            </span>
                                        )}
                                        {dur && pph > 0 && (
                                            <span className="px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs border border-white/25">
                                                {dur}h · {formatCurrency(pph, companyState || operatingLocationCode)}/hr
                                            </span>
                                        )}
                                        {firstSlot && lastSlot && (
                                            <span className="px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs border border-white/25">
                                                {firstSlot.date} → {lastSlot.date}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* ── Summary stats row ── */}
                            <div className="grid grid-cols-3 gap-3">
                                <div className="flex flex-col items-center justify-center py-3 px-2 bg-blue-50 rounded-xl border border-blue-100">
                                    <span className="text-blue-700 font-extrabold text-lg leading-none">{children.length}</span>
                                    <span className="text-blue-500 text-xs mt-1 font-medium text-center">Sessions</span>
                                </div>
                                <div className="flex flex-col items-center justify-center py-3 px-2 bg-indigo-50 rounded-xl border border-indigo-100">
                                    <span className="text-indigo-700 font-extrabold text-lg leading-none">{dur || '—'}h</span>
                                    <span className="text-indigo-500 text-xs mt-1 font-medium text-center">Per session</span>
                                </div>
                                <div className="flex flex-col items-center justify-center py-3 px-2 bg-violet-50 rounded-xl border border-violet-100">
                                    <span className="text-violet-700 font-extrabold text-base leading-none">{fmtAmt(parentTotal)}</span>
                                    <span className="text-violet-500 text-xs mt-1 font-medium text-center">Total</span>
                                </div>
                            </div>

                            {/* ── Sessions accordion ── */}
                            <div className="border border-slate-200 rounded-2xl overflow-hidden">

                                {/* Accordion header */}
                                <button
                                    type="button"
                                    onClick={() => setRecurringExpanded(p => !p)}
                                    className="w-full flex items-center justify-between px-4 py-3.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                                >
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                            <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-slate-700">
                                                {recurringExpanded ? 'Hide schedule' : 'View full schedule'}
                                            </p>
                                            {!recurringExpanded && firstSlot && (
                                                <p className="text-xs text-slate-400 mt-0.5">
                                                    First: {firstSlot.weekday}, {firstSlot.date} · {firstSlot.time}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center border transition-all duration-200 flex-shrink-0 ${recurringExpanded ? 'bg-blue-100 border-blue-200 rotate-180' : 'bg-white border-slate-200'}`}>
                                        <svg className={`w-3.5 h-3.5 transition-colors ${recurringExpanded ? 'text-blue-600' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </button>

                                {/* Expanded timeline */}
                                {recurringExpanded && (
                                    <div>
                                        {/* Column header */}
                                        <div className="flex items-center justify-between px-4 py-2 bg-white border-t border-slate-100">
                                            <span className="text-slate-400 text-xs font-semibold  tracking-wider">Session schedule</span>
                                            <span className="text-slate-400 text-xs font-semibold  tracking-wider">Amount</span>
                                        </div>

                                        {/* Timeline rows */}
                                        <div className="relative">
                                            {/* Vertical track line */}
                                            <div className="absolute left-[27px] top-0 bottom-0 w-px bg-blue-100" style={{ top: 8, bottom: 8 }} />

                                            {children.map((childOrder, index) => {
                                                const cl = childOrder?.lineItemList?.[0];
                                                const slot = formatSlotRange(cl);
                                                const amt = parseFloat(childOrder.dueAmount ?? childOrder.totalAmount ?? 0);
                                                const isLast = index === children.length - 1;
                                                const isFirst = index === 0;

                                                return (
                                                    <div
                                                        key={childOrder.primaryId || index}
                                                        className={`flex items-start gap-3 px-4 py-3 border-t border-slate-50 hover:bg-blue-50/40 transition-colors ${isFirst ? 'border-t-slate-100' : ''}`}
                                                    >
                                                        {/* Timeline dot */}
                                                        <div className="relative flex-shrink-0 flex items-center justify-center mt-0.5">
                                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold z-10 shadow-sm ${isFirst ? 'bg-blue-600 text-white' : isLast ? 'bg-indigo-500 text-white' : 'bg-white border-2 border-blue-200 text-blue-600'}`}>
                                                                {index + 1}
                                                            </div>
                                                        </div>

                                                        {/* Session info */}
                                                        <div className="flex-1 min-w-0">
                                                            {slot ? (
                                                                <>
                                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                                        <span className="text-xs font-bold text-slate-700">{slot.weekday},</span>
                                                                        <span className="text-xs font-semibold text-slate-700">{slot.date}</span>
                                                                        {isFirst && (
                                                                            <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">First</span>
                                                                        )}
                                                                        {isLast && children.length > 1 && (
                                                                            <span className="px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold">Last</span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-xs text-slate-400 mt-0.5">{slot.time}</p>
                                                                </>
                                                            ) : (
                                                                <p className="text-xs font-medium text-slate-600">Session {index + 1}</p>
                                                            )}
                                                        </div>

                                                        {/* Amount */}
                                                        <span className="text-xs font-bold text-slate-700 flex-shrink-0 mt-1">{fmtAmt(amt)}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Footer total row */}
                                        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-blue-100">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <span className="text-sm font-semibold text-blue-700">{children.length} sessions total</span>
                                            </div>
                                            <span className="text-sm font-extrabold text-slate-800">{fmtAmt(parentTotal)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* ── Divider ── */}
                <div className="border-t border-dashed border-slate-100 my-5" />

                {/* ── Location & Provider ── */}
                <div className="space-y-3">
                    {allAddresses.length > 0 && (
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                            <div className="flex items-center gap-2 mb-3">
                                <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="font-semibold text-slate-700 text-sm">Location</span>
                            </div>
                            <div className="space-y-3">
                                {allAddresses.map((addr, idx) => (
                                    <div key={idx} className={`p-3 rounded-lg border ${idx === 0 ? 'bg-white border-blue-100 shadow-sm' : 'bg-slate-50 border-slate-200'}`}>
                                        {addr.label && <p className="text-xs font-bold text-blue-600 mb-1 tracking-wide">{addr.label}</p>}
                                        <div className="text-sm space-y-0.5">
                                            {addr.streetName1 && <p className="text-slate-800 font-semibold">{addr.streetName1}</p>}
                                            {addr.streetName2 && <p className="text-slate-600">{addr.streetName2}</p>}
                                            {(addr.city || addr.state || addr.zipcode || addr.zipCode) && (
                                                <div className="flex flex-wrap gap-0.5 mt-1">
                                                    {addr.city && <span className="text-slate-500">{addr.city}</span>}
                                                    {addr.state && <span className="text-slate-500">, {addr.state}</span>}
                                                    {(addr.zipcode || addr.zipCode) && <span className="text-slate-500"> {addr.zipcode || addr.zipCode}</span>}
                                                </div>
                                            )}
                                            {addr.country && <p className="text-slate-400 text-xs">{addr.country}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {orderDetails.company?.name && (
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                            <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span className="font-semibold text-slate-700 text-sm">Provider</span>
                            </div>
                            <div className="flex items-center gap-2 pl-6">
                                {orderDetails.company?.logoImageContent?.displayLink && (
                                    <img src={orderDetails.company.logoImageContent.displayLink} alt="" className="w-7 h-7 rounded-full border border-slate-200 object-cover" />
                                )}
                                <span className="text-slate-700 text-sm font-medium">{orderDetails.company.name}</span>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default OrderDetailsCard;
